import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import {
  billingErrors,
  BillingForm,
  EMPTY_BILLING,
  formatIban,
  maskIban,
  normalizeIban,
  normalizeNif,
} from '../../../lib/billing';
import { Button, Input } from '../../design-system';
import type { HostBillingDetails } from '../../../types';

interface BillingDetailsCardProps {
  hostId: string;
  details: HostBillingDetails | null;
  onSaved: (details: HostBillingDetails) => void;
  /** Shown when matching needs these details — the host can't be booked without them. */
  emphasiseUrgency?: boolean;
}

function toForm(d: HostBillingDetails | null): BillingForm {
  if (!d) return EMPTY_BILLING;
  return {
    legal_name: d.legal_name,
    nif: d.nif,
    address_line: d.address_line,
    postal_code: d.postal_code,
    city: d.city,
    country: d.country,
    iban: formatIban(d.iban),
  };
}

/**
 * The details Sekretly needs to pay a host: the name and NIF on their recibo
 * verde, an address, and the IBAN the money goes to. Twice a month Sekretly
 * lists the host's finished trips; the host sends one recibo for the total and
 * is paid by bank transfer. Only the host and Sekretly's admins can read this.
 */
export function BillingDetailsCard({ hostId, details, onSaved, emphasiseUrgency = true }: BillingDetailsCardProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<BillingForm>(toForm(details));
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const errors = billingErrors(form);
  const set = (key: keyof BillingForm) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const err = (key: keyof BillingForm) => (showErrors ? errors[key] : undefined);

  async function save() {
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    setSaveError(null);
    const { data, error } = await supabase
      .from('host_billing_details')
      .upsert({
        host_id: hostId,
        legal_name: form.legal_name.trim(),
        nif: normalizeNif(form.nif),
        address_line: form.address_line.trim(),
        postal_code: form.postal_code.trim(),
        city: form.city.trim(),
        country: form.country.trim().toUpperCase(),
        iban: normalizeIban(form.iban),
      }, { onConflict: 'host_id' })
      .select('*')
      .single();
    setSaving(false);
    if (error || !data) {
      console.error('host_billing_details save failed:', error);
      setSaveError("We couldn't save your details. Check them and try again — if it keeps happening, contact support.");
      return;
    }
    onSaved(data as HostBillingDetails);
    setEditing(false);
    setShowErrors(false);
  }

  const urgent = !details && emphasiseUrgency;

  return (
    <section
      className={`rounded-2xl p-5 flex flex-col gap-4 border ${urgent ? 'bg-crimson/10 border-crimson/30' : 'bg-app-card border-app-border'}`}
    >
      <div>
        <h2 className="text-sm font-semibold text-app-fg">Getting paid</h2>
        <p className="text-xs text-app-muted mt-0.5 leading-relaxed">
          {details
            ? 'Twice a month we list your finished trips. You send us one recibo verde for the total, and we pay it by bank transfer.'
            : "Add your billing details so we can pay you. Until then you can't be matched with travelers."}
        </p>
      </div>

      {details && !editing ? (
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-success/15 flex items-center justify-center shrink-0">
            <i className="ti ti-check text-[--success-fg] text-base" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-app-fg">{details.legal_name} · NIF {details.nif}</p>
            <p className="text-xs text-app-muted">
              {details.address_line}, {details.postal_code} {details.city}
            </p>
            <p className="text-xs text-app-muted mt-0.5">IBAN {maskIban(details.iban)}</p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => { setForm(toForm(details)); setEditing(true); }}>Edit</Button>
        </div>
      ) : editing || !details ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => { e.preventDefault(); void save(); }}
          noValidate
        >
          <Input label="Full name (as on your recibo)" autoComplete="name" value={form.legal_name} onChange={set('legal_name')} error={err('legal_name')} disabled={saving} />
          <Input label="NIF" inputMode="numeric" autoComplete="off" value={form.nif} onChange={set('nif')} error={err('nif')} disabled={saving} />
          <Input label="Street address" autoComplete="street-address" value={form.address_line} onChange={set('address_line')} error={err('address_line')} disabled={saving} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Postal code" autoComplete="postal-code" value={form.postal_code} onChange={set('postal_code')} error={err('postal_code')} disabled={saving} />
            <Input label="City" autoComplete="address-level2" value={form.city} onChange={set('city')} error={err('city')} disabled={saving} />
          </div>
          <Input
            label="IBAN"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={form.iban}
            onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value.toUpperCase() }))}
            error={err('iban')}
            disabled={saving}
          />
          <p className="text-caption text-app-muted -mt-1">
            Only you and the Sekretly team can see these details. If you change your IBAN, we may contact you to confirm it before the next payment.
          </p>
          {saveError ? <p role="alert" className="text-xs text-crimson">{saveError}</p> : null}
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save billing details'}</Button>
            {details ? (
              <Button size="sm" type="button" variant="secondary" disabled={saving} onClick={() => { setEditing(false); setShowErrors(false); setSaveError(null); }}>Cancel</Button>
            ) : null}
          </div>
        </form>
      ) : null}
    </section>
  );
}

export default BillingDetailsCard;
